import type { TableRead, TypedFormula } from "./formula";
import { formulaTableReads } from "./formula";

export interface SheetLineageLookup {
  value: FieldRef | null;
  match: FieldRef;
  missColumn: string | null;
}

/** One column of a Source or Worksheet, by the table's id. */
export interface FieldRef {
  tableId: string;
  column: string;
}

/**
 * Where a Worksheet's values come from, as far as its settings say without
 * data: which Result columns are another table's column unchanged, what each
 * of its lookups looks up and where, and every column it reads. Sampling
 * traces lookup keys back to the Source columns they start from with it.
 */
export interface SheetLineage {
  /** Result columns (by lowercase name) that are another table's column unchanged. */
  fields: ReadonlyMap<string, FieldRef>;
  /**
   * Each lookup (a Lookup or a Formula lookup): the column it looks up, when
   * unchanged, and the column it matches; and the Field of this Worksheet
   * that holds its result and #N/A on a miss (`missColumn`), when one does,
   * so its #N/A count is its misses.
   */
  lookups: readonly SheetLineageLookup[];
  /** Every column of another table it reads. */
  reads: readonly FieldRef[];
}

export const EMPTY_LINEAGE: SheetLineage = {
  fields: new Map(),
  lookups: [],
  reads: [],
};

/** Every column a Formula reads, by name, lookup values included. */
export function formulaColumnNames(formula: TypedFormula): string[] {
  switch (formula.kind) {
    case "column":
      return [formula.name];
    case "unary":
      return formulaColumnNames(formula.operand);
    case "binary":
      return [
        ...formulaColumnNames(formula.left),
        ...formulaColumnNames(formula.right),
      ];
    case "call":
      return formula.args.flatMap(formulaColumnNames);
    case "lookup":
      return [
        ...formulaColumnNames(formula.value),
        ...(formula.notFound ? formulaColumnNames(formula.notFound) : []),
      ];
    case "aggregate":
      return formula.criteria.flatMap(({ value }) => formulaColumnNames(value));
    default:
      return [];
  }
}

/**
 * A Formula's value as another table's column unchanged, when it is one: a
 * bare reference, or a lookup's returned column. `resolve` places a
 * reference among the Worksheet's own Fields.
 */
export function formulaOrigin(
  formula: TypedFormula,
  resolve: (name: string) => FieldRef | null,
): FieldRef | null {
  if (formula.kind === "column") return resolve(formula.name);
  if (formula.kind === "lookup")
    return { tableId: formula.tableId, column: formula.returnColumn.name };
  return null;
}

/** The other table's columns a lookup or a Conditional aggregate reads. */
function tableReadColumns(read: TableRead): FieldRef[] {
  const columns =
    read.kind === "lookup"
      ? [read.matchColumn, read.returnColumn]
      : [
          ...read.criteria.map(({ column }) => column),
          ...(read.sumColumn ? [read.sumColumn] : []),
        ];
  return columns.map(({ name }) => ({ tableId: read.tableId, column: name }));
}

/**
 * Each Formula lookup, and each criteria of a Conditional aggregate (a
 * SUMIFS or COUNTIFS reaches the other table's rows by that key as a lookup
 * does), as a lineage lookup, and the table columns they read.
 */
export function formulaLookupLineage(
  formula: TypedFormula,
  resolve: (name: string) => FieldRef | null,
): { lookups: SheetLineage["lookups"]; reads: FieldRef[] } {
  const lookups = formulaTableReads(formula).flatMap(
    (read): SheetLineageLookup[] =>
      read.kind === "lookup"
        ? [
            {
              value: formulaOrigin(read.value, resolve),
              match: { tableId: read.tableId, column: read.matchColumn.name },
              missColumn: null,
            },
          ]
        : read.criteria.map(({ column, value }) => ({
            value: formulaOrigin(value, resolve),
            match: { tableId: read.tableId, column: column.name },
            missColumn: null,
          })),
  );
  return {
    lookups,
    reads: formulaTableReads(formula).flatMap(tableReadColumns),
  };
}
